import React from 'react'
import { Box, Typography, Avatar, Chip } from '@mui/material'
import { styled, keyframes } from '@mui/material/styles'
import { FavoriteBorder, RemoveRedEyeOutlined, AutoAwesome } from '@mui/icons-material'
import { Topic } from '@/types'
import { UserAvatar } from '../Avatar'
import { formattedNumber, getTopicNameFromTopic } from '@/utils/helper'

const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
`

const PatternCardContainer = styled(Box)({
  position: 'relative',
  borderRadius: '20px',
  background: '#ffffff',
  border: '2px solid rgba(139, 92, 246, 0.12)',
  overflow: 'hidden',
  cursor: 'pointer',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: `
    0 4px 16px rgba(139, 92, 246, 0.08),
    0 2px 8px rgba(0, 0, 0, 0.04)
  `,

  '&:hover': {
    transform: 'translateY(-8px)',
    borderColor: 'rgba(139, 92, 246, 0.3)',
    boxShadow: `
      0 20px 40px rgba(139, 92, 246, 0.2),
      0 8px 16px rgba(0, 0, 0, 0.08)
    `,

    '& .pattern-preview': {
      transform: 'scale(1.05)',
    },

    '& .geometric-overlay': {
      opacity: 0.8,
    },
  },
})

const PatternPreview = styled(Box)({
  position: 'relative',
  width: '100%',
  height: '240px',
  background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
  overflow: 'hidden',

  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.4s ease',
  },
})

const GeometricOverlay = styled(Box)({
  position: 'absolute',
  inset: 0,
  opacity: 0.4,
  transition: 'opacity 0.4s ease',
  pointerEvents: 'none',

  // Grid pattern
  backgroundImage: `
    linear-gradient(rgba(139, 92, 246, 0.08) 1px, transparent 1px),
    linear-gradient(90deg, rgba(139, 92, 246, 0.08) 1px, transparent 1px)
  `,
  backgroundSize: '20px 20px',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    right: '-50%',
    width: '100%',
    height: '100%',
    background: 'radial-gradient(circle, rgba(139, 92, 246, 0.2) 0%, transparent 70%)',
    animation: `${float} 8s ease-in-out infinite`,
  },
})

const PatternBadge = styled(Box)({
  position: 'absolute',
  top: '16px',
  left: '16px',
  padding: '6px 12px',
  borderRadius: '10px',
  background: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)',
  backdropFilter: 'blur(10px)',
  border: '1.5px solid rgba(255, 255, 255, 0.3)',
  boxShadow: '0 4px 16px rgba(139, 92, 246, 0.4)',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  zIndex: 2,

  '& svg': {
    fontSize: '16px',
    color: '#ffffff',
  },

  '& span': {
    fontSize: '12px',
    fontWeight: 700,
    color: '#ffffff',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
})

const CardContent = styled(Box)({
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
})

const PatternTitle = styled(Typography)({
  fontSize: '16px',
  fontWeight: 700,
  color: '#0f172a',
  letterSpacing: '-0.01em',
  lineHeight: 1.3,
  marginBottom: '4px',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
})

const CardFooter = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingTop: '12px',
  borderTop: '1px solid rgba(226, 232, 240, 0.6)',
})

const CreatorInfo = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
})

const CreatorAvatar = styled(Avatar)({
  width: '28px',
  height: '28px',
  border: '2px solid rgba(139, 92, 246, 0.2)',
})

const CreatorName = styled(Typography)({
  fontSize: '13px',
  fontWeight: 600,
  color: '#64748b',
})

const StatsContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
})

const StatBadge = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  color: '#94a3b8',
  fontSize: '13px',
  fontWeight: 600,

  '& svg': {
    fontSize: '16px',
  },
})

interface PatternCardProps {
  topic: Topic
  onClick: (topic: Topic) => void
}

const PatternCard: React.FC<PatternCardProps> = ({ topic, onClick }) => {
  const likes = Number(topic.totalLikes) || 0
  const views = Number(topic.visitorVisits || 0) + Number(topic.ownerVisits || 0)

  return (
    <PatternCardContainer className='pattern-card' onClick={() => onClick(topic)}>
      <PatternPreview className='pattern-preview'>
        <img
          src={topic.latestEgo?.photoAlbumURL || '/placeholder.png'}
          alt={topic.topicName}
          loading='lazy'
        />
        <GeometricOverlay className='geometric-overlay' />
        <PatternBadge>
          <AutoAwesome />
          <span>Pattern</span>
        </PatternBadge>
      </PatternPreview>

      <CardContent>
        <PatternTitle>{getTopicNameFromTopic(topic)}</PatternTitle>

        <CardFooter>
          <CreatorInfo>
            <UserAvatar
              borderRadius={100}
              name={topic.userName || 'Anonymous'}
              url={topic.profilePicThumbURL || '/placeholder.png'}
              size={28}
            />

            <CreatorName>{topic.userName || 'Anonymous'}</CreatorName>
          </CreatorInfo>

          <StatsContainer>
            <StatBadge>
              <FavoriteBorder />
              <span>{formattedNumber(likes)}</span>
            </StatBadge>
            <StatBadge>
              <RemoveRedEyeOutlined />
              <span>{formattedNumber(views)}</span>
            </StatBadge>
          </StatsContainer>
        </CardFooter>
      </CardContent>
    </PatternCardContainer>
  )
}

export default PatternCard
